/**
 * Report Analytics Hub — tabbed interface for KPI and Business Analysis.
 * Uses URL-based tab routing for deep-linkable tabs.
 * Keep-alive rendering: ALL tabs stay mounted (hidden via display:none).
 */
import React, { useMemo } from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ForecastAccuracy from './ForecastAccuracy';
import ServiceLevelKPI from './ServiceLevelKPI';
import StorePerformance from './StorePerformance';
import CategoryPerformance from './CategoryPerformance';
import ABCClassificationDashboard from '../Analytics/ABCClassificationDashboard';
import AIInsightsDashboard from '../Analytics/AIInsightsDashboard';

interface TabDef {
  label: string;
  path: string;
  Component: React.FC;
  group: 'kpi' | 'business';
}

const tabs: TabDef[] = [
  // KPI group
  { label: 'Forecast Accuracy', path: '/report-analytics/kpi/forecast-accuracy', Component: ForecastAccuracy, group: 'kpi' },
  { label: 'Service Level', path: '/report-analytics/kpi/service-level', Component: ServiceLevelKPI, group: 'kpi' },
  // Business Analysis group
  { label: 'Store Performance', path: '/report-analytics/business-analysis/store-performance', Component: StorePerformance, group: 'business' },
  { label: 'Category Performance', path: '/report-analytics/business-analysis/category-performance', Component: CategoryPerformance, group: 'business' },
  { label: 'ABC Classification', path: '/report-analytics/business-analysis/abc-classification', Component: ABCClassificationDashboard, group: 'business' },
  { label: 'AI Insights', path: '/report-analytics/business-analysis/ai-insights', Component: AIInsightsDashboard, group: 'business' },
];

const ReportAnalyticsHub: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const activeTabIndex = useMemo(() => {
    const idx = tabs.findIndex((t) => location.pathname === t.path);
    return idx >= 0 ? idx : 0;
  }, [location.pathname]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(tabs[newValue].path);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed header: title + tabs (do not scroll) */}
      <Box sx={{ flexShrink: 0 }}>
        {/* Page title */}
        <Box sx={{ mb: '20px' }}>
          <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
            Report Analytics
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-base)', color: 'var(--color-text-muted)', mt: 0.5, fontFamily: 'var(--font-body)' }}>
            KPI tracking, business analysis, and AI-powered insights
          </Typography>
        </Box>

        {/* Tab navigation */}
        <Box sx={{ borderBottom: '1px solid var(--color-border)', mb: 3 }}>
          <Tabs
            value={activeTabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, idx) => (
              <Tab
                key={tab.path}
                label={tab.label}
                sx={{
                  // Visual separator between KPI and Business Analysis groups
                  ...(idx === 2 ? { ml: 2, '&::before': { content: '""', position: 'absolute', left: '-8px', top: '25%', bottom: '25%', width: '1px', bgcolor: 'var(--color-border)' } } : {}),
                }}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      {/* Scrollable tab content area — only this scrolls */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {/* Keep-alive tab content */}
        {tabs.map((tab, idx) => {
          const TabComponent = tab.Component;
          return (
            <Box
              key={tab.path}
              sx={{ display: activeTabIndex === idx ? 'block' : 'none' }}
            >
              <TabComponent />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ReportAnalyticsHub;
